import React from "react";
import PropTypes from "prop-types";

import Modal from "./Modal";
import Button from "../Button";
import TextButton from "../TextButton";
import { errorShapeForErrorBanner } from "../ErrorBanner";
import { availableContentSizes, availableHeaderSizes } from "./Modal.styles";
import {
  cancelDefaultProps,
  cancelPropTypes,
  makeOptionPropsCompatible,
  primaryDefaultProps,
  primaryPropTypes,
  secondaryDefaultProps,
  secondaryPropTypes,
} from "./utils";

const SCROLL_PROP_TO_CLASS_MAP = {
  true: "tw-overflow-auto",
  false: "tw-overflow-hidden",
};

const DefaultFooter = ({
  primaryOption,
  primaryProps,
  onPrimary,
  isPrimaryLoading,
  secondaryOption,
  secondaryProps,
  onSecondary,
  isSecondaryLoading,
  cancelProps,
  onCancel,
  error,
}) => {
  const isSomethingLoading = isPrimaryLoading || isSecondaryLoading;

  return (
    <div className="tw-flex tw-flex-col tw-gap-16px tw-w-full">
      {error && (
        <div className="tw-rounded-4px tw-bg-semantic-red-5 tw-p-8px tw-text-14d tw-font-normal tw-text-semantic-red-100 tw-text-right">
          {error}
        </div>
      )}
      <div className="tw-flex tw-justify-between">
        <Button
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...cancelProps}
          size="medium"
          schema="tertiary"
          disabled={isSomethingLoading || cancelProps.disabled}
          onClick={onCancel}
          data-cy={cancelProps["data-cy"] || cancelProps.dataCy || "prompt-cancel-button"}
        >
          Cancel
        </Button>
        <div className="tw-flex tw-flex-row tw-space-x-[12px]">
          {secondaryOption && (
            <TextButton
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...secondaryProps}
              size="medium"
              disabled={isSomethingLoading || secondaryProps.disabled}
              isLoading={isSecondaryLoading}
              onClick={onSecondary}
              data-cy={secondaryProps["data-cy"] || secondaryProps.dataCy || "prompt-secondary-button"}
            >
              {secondaryOption}
            </TextButton>
          )}
          <Button
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...primaryProps}
            size="medium"
            disabled={isSomethingLoading || primaryProps.disabled}
            isLoading={isPrimaryLoading}
            onClick={onPrimary}
            data-cy={primaryProps["data-cy"] || primaryProps.dataCy || "prompt-primary-button"}
          >
            {primaryOption}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * @summary Extends GenericModal to offer a medium sized modal for title, 1-liner, arbitrary body and up to 2 CTAs.
 * @param {string} title - the modal's title.
 * @param {(string|JSX.Element|object)} description - subtitle for the modal.
 * @param {object[]} errors - errors to show below the description, if any. Use the `errorForBanner` helper from `v2/ErrorBanner` to create elements for this array.
 * @param {string} primaryOption - content for the primary button.
 * @param {string} primaryProps - props to be passed to the primary button. Check Button for available props.
 * @param {func} onPrimary - function for what happens clicking on primary button.
 * @param {bool} isPrimaryLoading - helper to mark the primary button as loading and disable others.
 * @param {string} secondaryOption - content for the secondary button.
 * @param {string} secondaryProps - props to be passed to the secondary button. Check Text Button for available props.
 * @param {func} onSecondary - function for what happens clicking on secondary button.
 * @param {bool} isSecondaryLoading - helper to mark the secondary button as loading and disable others.
 * @param {string} cancelProps - props to be passed to the cancel button. Check Button for available props.
 * @param {func} onCancel - function for what happens clicking outside, cancel or close button.
 * @param {node} children - elements within the body
 * @param {bool} scroll - defines if component is scrollable.
 * @param {bool} show - defines if modal is visible.
 * @param {JSX.Element} header - the modal's header
 * @param {JSX.Element} footer - the modal's footer
 * @param {string} contentSize - defines the width of the modal's content.
 * @param {string} headerSize - defines the size of the header.
 * @param {bool} closeOnEscape - Close modal when press ESC
 * @param {bool} closeOnClickOutside - Close modal when click outside
 * */
const FloatingForm = ({
  title,
  description,
  errors,
  primaryOption,
  onPrimary,
  isPrimaryLoading,
  secondaryOption,
  onSecondary,
  isSecondaryLoading,
  cancelProps,
  onCancel,
  show,
  children,
  scroll,
  header,
  footer,
  footerError,
  contentSize,
  headerSize,
  closeOnEscape,
  closeOnClickOutside,
  modalBodyClassName,
  ...optionProps
}) => {
  const { primaryProps, secondaryProps } = makeOptionPropsCompatible(optionProps);

  // Hack for the _very specific use case_ of using a Dropdown inside of a tiny
  // FloatingForm Modal: in that case, the scroll="false" behavior is not
  // desirable (because the options menu becomes invisible), but neither is
  // scroll="true", because then the user only has a _tiny_ window to scroll
  // through the options. In this case, we want the default behavior of letting
  // the contents overflow the modal, so this is an escape hatch for just that:
  // if an "invalid" value is passed in to the `scroll` prop, we don't mess with
  // the overflow property at all.
  const overflow = SCROLL_PROP_TO_CLASS_MAP[scroll] || "";

  return (
    <Modal
      show={show}
      onHide={onCancel}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-min-h-full tw-max-h-full tw-overflow-c"
      contentClassName="tw-space-y-[32px] tw-m-auto"
      contentSize={contentSize}
      closeOnEscape={closeOnEscape}
      closeOnClickOutside={closeOnClickOutside}
    >
      {header ? (
        <Modal.Header>{header}</Modal.Header>
      ) : (
        <Modal.Header title={title} description={description} errors={errors} size={headerSize} closeButton />
      )}
      <Modal.Body className={`${overflow} tw-max-h-[50vh] ${modalBodyClassName}`}>{children}</Modal.Body>
      <Modal.Footer className="tw-flex tw-flex-row tw-justify-between">
        {footer || (
          <DefaultFooter
            primaryOption={primaryOption}
            primaryProps={primaryProps}
            onPrimary={onPrimary}
            isPrimaryLoading={isPrimaryLoading}
            secondaryOption={secondaryOption}
            secondaryProps={secondaryProps}
            onSecondary={onSecondary}
            isSecondaryLoading={isSecondaryLoading}
            cancelProps={cancelProps}
            onCancel={onCancel}
            error={footerError}
          />
        )}
      </Modal.Footer>
    </Modal>
  );
};

DefaultFooter.propTypes = {
  primaryOption: PropTypes.string,
  primaryProps: primaryPropTypes,
  onPrimary: PropTypes.func,
  isPrimaryLoading: PropTypes.bool,
  secondaryOption: PropTypes.string,
  secondaryProps: secondaryPropTypes,
  onSecondary: PropTypes.func,
  isSecondaryLoading: PropTypes.bool,
  cancelProps: cancelPropTypes,
  onCancel: PropTypes.func,
  error: PropTypes.string,
};

DefaultFooter.defaultProps = {
  primaryOption: null,
  primaryProps: primaryDefaultProps,
  onPrimary: null,
  isPrimaryLoading: false,
  secondaryOption: null,
  secondaryProps: secondaryDefaultProps,
  onSecondary: null,
  isSecondaryLoading: false,
  cancelProps: cancelDefaultProps,
  onCancel: null,
  error: null,
};

FloatingForm.propTypes = {
  children: PropTypes.node,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.element]),
  errors: PropTypes.arrayOf(errorShapeForErrorBanner),
  primaryOption: PropTypes.string,
  primaryProps: primaryPropTypes,
  onPrimary: PropTypes.func,
  isPrimaryLoading: PropTypes.bool,
  secondaryOption: PropTypes.string,
  secondaryProps: secondaryPropTypes,
  onSecondary: PropTypes.func,
  isSecondaryLoading: PropTypes.bool,
  cancelProps: primaryPropTypes,
  onCancel: PropTypes.func,
  scroll: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  show: PropTypes.bool,
  title: PropTypes.string,
  header: PropTypes.node,
  footer: PropTypes.node,
  footerError: PropTypes.string,
  contentSize: PropTypes.oneOf(availableContentSizes),
  headerSize: PropTypes.oneOf(availableHeaderSizes),
  closeOnEscape: PropTypes.bool,
  closeOnClickOutside: PropTypes.bool,
  modalBodyClassName: PropTypes.string,
};

FloatingForm.defaultProps = {
  children: null,
  description: null,
  errors: [],
  primaryOption: null,
  primaryProps: primaryDefaultProps,
  onPrimary: null,
  isPrimaryLoading: false,
  secondaryOption: null,
  secondaryProps: secondaryDefaultProps,
  onSecondary: null,
  isSecondaryLoading: false,
  cancelProps: cancelDefaultProps,
  onCancel: null,
  scroll: true,
  show: false,
  title: null,
  header: null,
  footer: null,
  footerError: null,
  contentSize: "medium",
  headerSize: "medium",
  closeOnEscape: false,
  closeOnClickOutside: false,
  modalBodyClassName: "",
};

export default FloatingForm;
